import { useEffect, useState } from "react";

import PublishIcon from "@mui/icons-material/Publish";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listOrganizations } from "@/api/organizations";
import {
  type BulkPublishResult,
  bulkPublishPlannings,
} from "@/api/planning";

type Props = {
  open: boolean;
  defaultYear: number;
  defaultMonth: number;
  onClose: () => void;
};

export function BulkPublishDialog({
  open,
  defaultYear,
  defaultMonth,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<BulkPublishResult | null>(null);

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setYear(defaultYear);
      setMonth(defaultMonth);
      setResult(null);
      setSelected(new Set());
    }
  }, [open, defaultYear, defaultMonth]);

  useEffect(() => {
    if (orgs.data && selected.size === 0 && !result) {
      setSelected(new Set(orgs.data.items.map((o) => o.id)));
    }
  }, [orgs.data, selected.size, result]);

  const publishMutation = useMutation({
    mutationFn: () =>
      bulkPublishPlannings(
        Array.from(selected).map((organizationId) => ({
          organization_id: organizationId,
          year,
          month,
        })),
      ),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ["planning"] });
    },
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const close = () => {
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t("bulkPublish.title")}</DialogTitle>
      <DialogContent>
        {!result && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("bulkPublish.subtitle")}
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label={t("bulkPublish.year")}
                type="number"
                size="small"
                value={year}
                onChange={(e) => setYear(Number(e.target.value) || defaultYear)}
                inputProps={{ min: 2000, max: 2100 }}
              />
              <TextField
                label={t("bulkPublish.month")}
                type="number"
                size="small"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value) || defaultMonth)}
                inputProps={{ min: 1, max: 12 }}
              />
            </Stack>

            {orgs.isLoading && <CircularProgress size={20} />}
            {orgs.data && (
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: 280,
                  overflowY: "auto",
                  p: 1,
                }}
              >
                {orgs.data.items.map((o) => (
                  <FormControlLabel
                    key={o.id}
                    control={
                      <Checkbox
                        checked={selected.has(o.id)}
                        onChange={() => toggle(o.id)}
                        size="small"
                      />
                    }
                    label={
                      <span>
                        <span style={{ fontFamily: "monospace", marginRight: 8 }}>
                          {o.code}
                        </span>
                        {o.name}
                      </span>
                    }
                    sx={{ display: "flex" }}
                  />
                ))}
              </Box>
            )}
          </Stack>
        )}

        {result && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="success">
              {t("bulkPublish.published", { count: result.published })}
            </Alert>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color="primary"
                label={`${t("bulkPublish.requested")}: ${result.requested}`}
              />
              <Chip
                size="small"
                color="success"
                label={`${t("bulkPublish.publishedShort")}: ${result.published}`}
              />
              <Chip
                size="small"
                color="default"
                label={`${t("bulkPublish.alreadyPublished")}: ${result.skipped_already_published}`}
              />
              <Chip
                size="small"
                color="warning"
                label={`${t("bulkPublish.locked")}: ${result.skipped_locked}`}
              />
              {result.errors.length > 0 && (
                <Chip
                  size="small"
                  color="error"
                  label={`${t("bulkPublish.errors")}: ${result.errors.length}`}
                />
              )}
            </Stack>
            {result.errors.length > 0 && (
              <Alert severity="warning">
                {result.errors.map((e) => `${e.year}-${e.month}: ${e.detail}`).join(" · ")}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>
          {result ? t("common.close") : t("common.cancel")}
        </Button>
        {!result && (
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => publishMutation.mutate()}
            disabled={publishMutation.isPending || selected.size === 0}
          >
            {t("bulkPublish.publishCta", { count: selected.size })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
